import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';

import { PrivateDocumentForm } from '@/shared/lib/forms/documents/private';
import {
  FormLabelItem,
  HelperRenderType,
  LABELS_ARR,
  ValueType,
} from '@/shared/lib/forms/form-wrapper.types';

import { PrivateDocFormKeys } from '../types';

export type FormFieldConfig = ValueType<HelperRenderType, unknown, unknown>;
export type FormConfig = Record<string, FormFieldConfig>;
export type FormatLabelParam =
  | string
  | { prefix: string; postfix: string; fieldKey: string }
  | undefined;

export const usePrivateDocFormFields = () => {
  const { t } = useTranslation('documents');

  const { formData, formKeys } = useMemo(() => {
    const baseForm = PrivateDocumentForm as Record<PrivateDocFormKeys, FormFieldConfig> | undefined;

    if (!baseForm) {
      return { formData: {}, formKeys: [] };
    }

    const entries = Object.entries(baseForm).filter(
      ([, cfg]: [string, FormFieldConfig]) =>
        cfg && !LABELS_ARR.includes(cfg.type as unknown as FormLabelItem),
    );

    return {
      formData: Object.fromEntries(entries) as FormConfig,
      formKeys: entries.map(([key]) => key) as Array<PrivateDocFormKeys>,
    };
  }, []);

  const formatLabel = (label: FormatLabelParam) => {
    if (!label) return '';

    // TODO: remove label replacing after implementing Pid Info page (WA-4404)
    if (typeof label === 'string') {
      return t(label).replace(/^\*/, '');
    }

    return t(label.postfix);
  };

  return useMemo(
    () => ({
      formKeys,
      formData,
      formatLabel,
    }),
    [formKeys, formData, formatLabel],
  );
};
